import { useCallback, useEffect, useState, useRef } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import axios from "axios";
import { BASE_URL, REFERRAL_BASE_URL } from "../api";
import MessageToster from "../component/MessageToster";
import Loader from "../component/Loader";
import { getAuthToken, useHandleUnauthorized } from "../token";
import { EMAIL_REGEX } from "../utils/regex";
import { useHandleError } from "../hooks/useHandleError";
import copy from "copy-to-clipboard";
import { TIMEZONES } from "../utils/timezones";
import { getApiHeaders } from "../utils/helpers";
import TwoFactorModal from "../component/TwoFactorModal";

const getTodayDateString = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

export default function Profile() {
    const token = getAuthToken();
    const handleError = useHandleError();
    const handleUnauthorized = useHandleUnauthorized();
    const [loading, setLoading] = useState(false);
    const [verror, setVerror] = useState('');

    // ── 2FA Setup ──────────────────────────────────────────────────────────
    const [twoFaData, setTwoFaData] = useState(null);   // { otpauth_url, secret_key }
    const [twoFaLoading, setTwoFaLoading] = useState(false);
    const [disableOtp, setDisableOtp] = useState(["", "", "", "", "", ""]);
    const disableOtpRefs = useRef([]);

    const handleDisableOtpInput = (e, idx) => {
        const val = e.target.value.replace(/\D/g, "").slice(-1);
        const newOtp = [...disableOtp];
        newOtp[idx] = val;
        setDisableOtp(newOtp);
        if (val && idx < 5) {
            disableOtpRefs.current[idx + 1]?.focus();
        }
    };

    const handleDisableOtpKeyDown = (e, idx) => {
        if (e.key === "Backspace" && !disableOtp[idx] && idx > 0) {
            disableOtpRefs.current[idx - 1]?.focus();
        }
    };

    const handleDisableOtpPaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
        if (pasteData) {
            const newOtp = [...disableOtp];
            for (let i = 0; i < pasteData.length; i++) {
                if (i < 6) newOtp[i] = pasteData[i];
            }
            setDisableOtp(newOtp);
            const nextEmpty = newOtp.findIndex((v) => !v);
            disableOtpRefs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus();
        }
    };

    const [country, setCountry] = useState([]);
    const [disableFields, setDisableFields] = useState({
        language: false,
        timezone: false,
        timefromate: false,
        dateformate: false
    });

    const [input, setInput] = useState({
        semail: "",
        country: "",
        countrycode: "",
        phone: "",
        dob: "",
        gender: "",
        email: "",
        // skypid: "",
        nationality: "",
        address1: "",
        address2: "",
        state: "",
        city: "",
        pin_code: "",
        pobox: "",
        language: "",
        timezone: "",
        timefromate: "",
        dateformate: "",
        loginid: "",
        two_fa_status: 0
    })
    const [validations, setValidations] = useState({
        semail: "",
        country: "",
        countrycode: "",
        phone: "",
        dob: "",
        gender: "",
        // skypid: "",
        nationality: "",
        address1: "",
        address2: "",
        state: "",
        city: "",
        pin_code: "",
        pobox: "",
        language: "",
        timezone: "",
        timefromate: "",
        dateformate: ""
    });


    const referralUrlValue = input.loginid ? REFERRAL_BASE_URL + input.loginid : '';

    const handle2FASetup = async () => {
        setTwoFaLoading(true);
        try {
            const res = await axios.get(
                `${BASE_URL}/account/generate_private_key_2fa`,
                { headers: getApiHeaders() }
            );

            if (res.data.code === 200) {
                setTwoFaData(res.data);

                const modalEl = document.getElementById("twoFactorModal");
                if (modalEl && window.bootstrap?.Modal) {
                    const bsModal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
                    bsModal.show();
                }
            } else {
                MessageToster("error", res.data.message || "Failed to generate 2FA key.");
            }
        } catch (err) {
            MessageToster("error", err.message || "Something went wrong.");
        } finally {
            setTwoFaLoading(false);
        }
    };

    const confirmDisable2FA = async () => {
        const code = disableOtp.join("");
        if (code.length < 6) {
            MessageToster("error", "Please enter a valid 6-digit OTP.");
            return;
        }

        setTwoFaLoading(true);
        try {
            const res = await axios.post(
                `${BASE_URL}/account/verify_otp_2fa`,
                {
                    enable: false,
                    otp: code,
                },
                { headers: getApiHeaders() }
            );

            if (res.data.code === 200) {
                MessageToster("success", res.data.message || "2FA has been disabled.");
                getProfileData();

                const modalEl = document.getElementById("disable2FaModal");
                if (modalEl && window.bootstrap?.Modal) {
                    window.bootstrap.Modal.getOrCreateInstance(modalEl).hide();
                }
                setDisableOtp(["", "", "", "", "", ""]);
            } else {
                MessageToster("error", res.data.message || "Failed to disable 2FA.");
            }
        } catch (err) {
            MessageToster("error", err.message || "Something went wrong.");
        } finally {
            setTwoFaLoading(false);
        }
    };

    const copyTextToClipboard = async (textToCopy, messageType) => {
        try {
            const success = copy(textToCopy);
            if (success) {
                MessageToster("success", messageType);
            } else {
                MessageToster("error", "Failed to copy. Please try again.");
            }
        } catch (err) {
            console.error('Failed to copy: ', err);
            MessageToster("error", 'Failed to copy. Please try again.');
        }
    };

    const copyCode = () => {
        copyTextToClipboard(input.loginid, `Referral Code Copied: ${input.loginid}`);
    };

    const copyLink = () => {
        copyTextToClipboard(referralUrlValue, `Referral Link Copied: ${referralUrlValue}`);
    };

    // const handelchange = (e) => {
    //     const { name, value } = e.target;
    //     setInput({ ...input, [name]: value });
    //     setValidations({ ...validations, [name]: false });
    // };

    const getSelectedCountry = () => {
        return country?.find(c => c.country_code === input.countrycode);
    };

    const handelchange = (e) => {
        const { name, value } = e.target;

        if (name === "phone") {
            const selected = getSelectedCountry();
            const maxLength = selected?.phone_code_length || 10;

            const onlyNums = value.replace(/\D/g, "");

            if (onlyNums.length > maxLength) return;

            setInput(prev => ({ ...prev, phone: onlyNums }));

            const phoneRegex = new RegExp(`^\\d{${maxLength}}$`);

            if (!phoneRegex.test(onlyNums)) {
                setValidations(prev => ({
                    ...prev,
                    phone: `Phone number must be exactly ${maxLength} digits`
                }));
            } else {
                setValidations(prev => ({ ...prev, phone: "" }));
            }

            return;
        }

        if (name === "countrycode") {
            setInput(prev => ({ ...prev, countrycode: value, phone: "" }));

            setValidations(prev => ({ ...prev, phone: "" }));
            return;
        }

        if (name === "dob") {
            const todayStr = getTodayDateString();
            if (value && value > todayStr) {
                setValidations(prev => ({ ...prev, dob: true }));
                setVerror("Date of birth cannot be a future date.");
            } else {
                setValidations(prev => ({ ...prev, dob: false }));
                setVerror("");
            }
            setInput(prev => ({ ...prev, dob: value }));
            return;
        }

        setInput({ ...input, [name]: value });
        setValidations({ ...validations, [name]: false });
    };

    const onSubmitForm = async () => {
        if (input.dob) {
            const todayStr = getTodayDateString();
            if (input.dob > todayStr) {
                setValidations(prev => ({ ...prev, dob: true }));
                setVerror("Date of birth cannot be a future date.");
                return;
            }
        }

        if (input.semail) {
            if (!EMAIL_REGEX.test(input.semail)) {
                setValidations(prev => ({ ...prev, semail: true }));
                setVerror("Please enter a valid secondary email address.");
                return;
            }
        }

        const selected = getSelectedCountry();
        const maxLength = selected?.phone_code_length || 10;

        if (!input.phone || input.phone.length !== maxLength) {
            setValidations(prev => ({
                ...prev,
                phone: `Phone number must be exactly ${maxLength} digits`
            }));
            return;
        }


        setLoading(true);

        await updateProfile(input);

        setDisableFields({
            language: input.language !== "",
            timezone: input.timezone !== "",
            timefromate: input.timefromate !== "",
            dateformate: input.dateformate !== ""
        });
    }

    const updateProfile = async (input) => {
        const dataofmid = sessionStorage.getItem('mid');
        const dob = new Date(input.dob);
        const day = String(dob.getDate()).padStart(2, '0');
        const month = String(dob.getMonth() + 1).padStart(2, '0');
        const year = dob.getFullYear();

        const bodyData = {
            mid: Number(dataofmid),
            mobileno: input.phone,
            country: input.country,
            country_code: input.countrycode,
            secondary_email: input.semail,
            dob: `${day}-${month}-${year}`,
            gender: input.gender,
            // skype_id: input.skypid,
            nationality: input.nationality,
            address_1: input.address1,
            address_2: input.address2,
            state: input.state,
            city: input.city,
            pin_code: input.pin_code,
            po_box: input.pobox,
            language: input.language,
            timezone: input.timezone,
            time_format: input.timefromate,
            date_format: input.dateformate,
        };

        try {
            setLoading(true);
            const res = await axios.post(
                `${BASE_URL}/account/update_profile`,
                bodyData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (res.data.code === 200) {
                MessageToster("success", res.data.message);
            } else {
                MessageToster("error", res.data.message);
            }
        } catch (err) {
            handleError(err);

        } finally {
            setLoading(false);
        }
    };

    const getProfileData = useCallback(async () => {
        const dataofmid = sessionStorage.getItem("mid");
        const bodyData = { mid: Number(dataofmid) };

        try {
            setLoading(true);
            const res = await axios.get(`${BASE_URL}/account/get_user_data`, { headers: getApiHeaders() });
            // const res = await axios.post(
            //     `${BASE_URL}/account/get_user_data`,
            //     bodyData,
            //     {
            //         headers: {
            //             Authorization: `Bearer ${token}`,
            //             "Content-Type": "application/json",
            //         },
            //     }
            // );

            if (res.data.code === 200) {
                setInput(prev => ({
                    ...prev,
                    fname: res.data.data.fname,
                    lname: res.data.data.lname,
                    mobileno: res.data.data.mobileno,
                    semail: res.data.data.secondary_email,
                    email: res.data.data.email || "",
                    country: res.data.data.country,
                    countrycode: res.data.data.country_code,
                    phone: res.data.data.mobileno,
                    dob: res.data.data.dob,
                    gender: res.data.data.gender,
                    // skypid: res.data.data.skype_id,
                    nationality: res.data.data.nationality,
                    address1: res.data.data.address_1,
                    address2: res.data.data.address_2,
                    state: res.data.data.state,
                    city: res.data.data.city,
                    pin_code: res.data.data.pin_code,
                    pobox: res.data.data.po_box,
                    language: res.data.data.language,
                    timezone: res.data.data.timezone,
                    timefromate: res.data.data.time_format,
                    dateformate: res.data.data.date_format,
                    loginid: res.data.data.loginid,
                    two_fa_status: (res.data.data.is_2fa_enabled || res.data.data.two_fa_status || res.data.data.is_2fa || res.data.data.two_factor_status) ? 1 : 0
                }));

                setDisableFields({
                    language: res.data.data.language !== "",
                    timezone: res.data.data.timezone !== "",
                    timefromate: res.data.data.time_format !== "",
                    dateformate: res.data.data.date_format !== ""
                });

                // MessageToster("success", res.data.message);
            } else {
                MessageToster("error", res.data.message);
            }
        } catch (err) {
            handleError(err);

        } finally {
            setLoading(false);
        }
    }, [token, handleUnauthorized]);

    const getCountryData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${BASE_URL}/account/country_data`, { headers: getApiHeaders() });
            // setCountry(res.data.country_name || []);
            setCountry(res.data.country_data);

        } catch (err) {
            // MessageToster("error", err.message || "Something went wrong");
            handleError(err);
        } finally {
            setLoading(false);
        }
    }, [])

    useEffect(() => {
        getProfileData()
        getCountryData()
    }, [getProfileData, getCountryData])

    const copytext = (text) => {
        copy(text);
        MessageToster("success", "Copied!");
    };

    const seletedCountryLenght = getSelectedCountry()?.phone_code_length || 10

    return (
        <>
            <div id="layout-wrapper">
                {loading &&
                    <Loader />
                }
                <Topbar />
                <Sidebar />
                <div className="main-content">
                    <div className="page-content">
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-12">
                                    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                        <h2 className="fw-700 mb-sm-0">Profile</h2>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="row g-3 gx-4">
                                                <div className="col-12 mb-2">
                                                    <h1 className="modal-title">
                                                        Personal Information
                                                    </h1>
                                                </div>
                                                <div className="col-xl-4 col-lg-6 col-md-6">
                                                    <label className="form-label ">
                                                        First Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="fname"
                                                        name="fname"
                                                        autoComplete="off"
                                                        placeholder="Enter Your First Name"
                                                        value={input.fname}
                                                        onChange={handelchange}
                                                        disabled
                                                    />
                                                    {validations.fname &&
                                                        verror && <>
                                                            <p className='text-danger mb-0'>{verror}</p>
                                                        </>
                                                    }
                                                </div>
                                                <div className="col-xl-4 col-lg-6 col-md-6">
                                                    <label className="form-label ">
                                                        Last Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="lname"
                                                        name="lname"
                                                        autoComplete="off"
                                                        placeholder="Enter Your Last Name"
                                                        value={input.lname}
                                                        onChange={handelchange}
                                                        disabled
                                                    />
                                                    {validations.lname &&
                                                        verror && <>
                                                            <p className='text-danger mb-0'>{verror}</p>
                                                        </>
                                                    }
                                                </div>
                                                <div className="col-xl-4 col-lg-6 col-md-6">
                                                    <label className="form-label">
                                                        Primary E-mail Address
                                                    </label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        id="pemail"
                                                        value={input.email || sessionStorage.getItem('email')}
                                                        // value={sessionStorage.getItem('email')}
                                                        placeholder=""
                                                        disabled
                                                    />

                                                </div>
                                                {/* <div className="col-xl-4 col-lg-6 col-md-6">
                                                    <label className="form-label">
                                                        Secondary E-mail Address
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="semail"
                                                        name="semail"
                                                        placeholder="Enter Your Secondary Email Address"
                                                        value={input.semail}
                                                        onChange={handelchange}
                                                    />
                                                    {validations.semail &&
                                                        verror && <>
                                                            <p className='text-danger mb-0'>{verror}</p>
                                                        </>
                                                    }
                                                </div> */}
                                                <div className="col-xl-4 col-lg-6 col-md-6">
                                                    <label htmlFor="usernumber" className="form-label">
                                                        Country
                                                    </label>
                                                    <select
                                                        id="country"
                                                        name="country"
                                                        className="form-select"
                                                        value={input.country}
                                                        onChange={handelchange}
                                                    >
                                                        <option value="">select value</option>
                                                        {country.length > 0 &&
                                                            country.map((i, index) => (
                                                                <option key={index} value={i.country_name}>
                                                                    {i.country_name}
                                                                </option>
                                                            ))
                                                        }
                                                    </select>

                                                    {validations.country &&
                                                        verror && <>
                                                            <p className='text-danger mb-0'>{verror}</p>
                                                        </>
                                                    }
                                                </div>
                                                <div className="col-xl-4 col-lg-6 col-md-6">
                                                    <div className="d-flex gap-2 flex-wrap flex-md-nowrap">
                                                        <div className="w-50 country_input">
                                                            <label htmlFor="usernumber" className="form-label">
                                                                Country Code
                                                            </label>
                                                            <select
                                                                id="countrycode"
                                                                name="countrycode"
                                                                className="form-select"
                                                                value={input.countrycode}
                                                                onChange={(e) => handelchange(e)}
                                                            >
                                                                <option value="" disabled hidden>Code</option>
                                                                {Array.isArray(country) &&
                                                                    country.map((c, index) => (
                                                                        <option key={index} value={c.country_code}>
                                                                            {c.country_code} (+{c.phone_code})
                                                                        </option>
                                                                    ))
                                                                }
                                                            </select>
                                                            {validations.countrycode &&
                                                                verror && <>
                                                                    <p className='text-danger mb-0'>{verror}</p>
                                                                </>
                                                            }
                                                        </div>
                                                        <div className="w-100">
                                                            <label htmlFor="usernumber" className="form-label">
                                                                Phone Number
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                id="phone"
                                                                name="phone"
                                                                autoComplete="off"
                                                                placeholder="Enter Your Phone No"
                                                                value={input.phone}
                                                                onChange={handelchange}
                                                                disabled={!input.countrycode}
                                                                maxLength={seletedCountryLenght}
                                                            />
                                                            {/* {validations.phone &&
                                                                verror && <>
                                                                    <p className='text-danger mb-0'>{verror}</p>
                                                                </>
                                                            } */}
                                                            {validations.phone && (
                                                                <p className="text-danger mb-0">{validations.phone}</p>
                                                            )}

                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-xl-4 col-lg-6 col-md-6">
                                                    <label className="form-label ">
                                                        Date of Birth
                                                    </label>
                                                    <input
                                                        type="date"
                                                        max={getTodayDateString()}
                                                        className="form-control"
                                                        id="dob"
                                                        name="dob"
                                                        autoComplete="off"
                                                        placeholder="Enter Your Date of Birth"
                                                        value={input.dob}
                                                        onChange={handelchange}
                                                    />
                                                    {validations.dob && verror && (
                                                        <p className='text-danger mb-0 font-size-12 mt-1'>{verror}</p>
                                                    )}
                                                </div>
                                                <div className="col-xl-4 col-lg-6 col-md-6">
                                                    <div className="mb-3 col-12 ">
                                                        <label htmlFor="usernumber" className="form-label">
                                                            Gender
                                                        </label>
                                                        <select
                                                            id="gender"
                                                            name="gender"
                                                            className="form-select"
                                                            value={input.gender}
                                                            onChange={(e) => handelchange(e)}
                                                        >
                                                            <option value="" disabled hidden>select value</option>
                                                            <option value="male">Male</option>
                                                            <option value="female">Female</option>
                                                        </select>
                                                        {validations.gender &&
                                                            verror && <>
                                                                <p className='text-danger mb-0'>{verror}</p>
                                                            </>
                                                        }
                                                    </div>
                                                </div>
                                                {/* <div className="col-lg-4 col-md-6">
                                                    <label className="form-label ">
                                                        Skype ID
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="skypid"
                                                        name="skypid"
                                                        autoComplete="off"
                                                        placeholder="Enter Your Skype ID"
                                                        value={input.skypid}
                                                        onChange={handelchange}
                                                    />
                                                    {validations.skypid &&
                                                        verror && <>
                                                            <p className='text-danger mb-0'>{verror}</p>
                                                        </>
                                                    }
                                                </div> */}
                                                <div className="col-xl-4 col-lg-6 col-md-6">
                                                    <label className="form-label ">
                                                        Nationality
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="nationality"
                                                        name="nationality"
                                                        autoComplete="off"
                                                        placeholder="Enter Your Nationality"
                                                        value={input.nationality}
                                                        onChange={handelchange}
                                                    />
                                                    {validations.nationality &&
                                                        verror && <>
                                                            <p className='text-danger mb-0'>{verror}</p>
                                                        </>
                                                    }
                                                </div>
                                                <div className="col-xl-4 col-lg-6 col-md-6">
                                                    <label className="form-label" htmlFor="referralcode">
                                                        Referral Code
                                                    </label>
                                                    <div className="input-group">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            id="referralcode"
                                                            name="referralcode"
                                                            autoComplete="off"
                                                            placeholder="Referral Code"
                                                            value={input.loginid}
                                                            readOnly
                                                        />
                                                        <button
                                                            className="btn btn-primary waves-effect waves-light"
                                                            type="button"
                                                            // onClick={() => copyReferralLink(input.loginid)}
                                                            onClick={copyCode}
                                                        >
                                                            <i className="bx bxs-copy" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="col-xl-4 col-lg-6 col-md-6">
                                                    <label className="form-label" htmlFor="referralurl">
                                                        Referral URL
                                                    </label>
                                                    <div className="input-group">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            id="referralurl"
                                                            name="referralurl"
                                                            autoComplete="off"
                                                            placeholder="Referral URL"
                                                            value={referralUrlValue}
                                                            readOnly
                                                        />
                                                        <button
                                                            className="btn btn-primary waves-effect waves-light"
                                                            type="button"
                                                            onClick={copyLink}
                                                        >
                                                            <i className="bx bxs-copy" />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="col-12 my-2">
                                                    <hr className="my-2" />
                                                </div>

                                                <div className="col-12 mb-2">
                                                    <h1 className="modal-title">
                                                        Address Details
                                                    </h1>
                                                </div>
                                                <div className="col-sm-6">
                                                    <div className="mb-1">
                                                        <label className="form-label">
                                                            Apartment, Suite or Floor
                                                        </label>
                                                        <textarea className="form-control"
                                                            id="address1"
                                                            name="address1"
                                                            placeholder="apartment, suite or Floor"
                                                            rows="1"
                                                            cols="50"
                                                            value={input.address1}
                                                            onChange={handelchange}
                                                        >
                                                        </textarea>
                                                    </div>
                                                </div>
                                                <div className="col-sm-6">
                                                    <div className="mb-1">
                                                        <label className="form-label">
                                                            Street Address                                                        </label>
                                                        <textarea
                                                            className="form-control"
                                                            id="address2"
                                                            name="address2"
                                                            placeholder="Street address"
                                                            rows="1"
                                                            cols="50"
                                                            value={input.address2}
                                                            onChange={handelchange}
                                                        >
                                                        </textarea>
                                                    </div>
                                                </div>
                                                <div className="col-sm-6">
                                                    <div className="mb-1">
                                                        <label className="form-label">
                                                            City
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="city"
                                                            id="city"
                                                            value={input.city}
                                                            onChange={handelchange}
                                                            autoComplete="off"
                                                            placeholder="City"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-sm-6">
                                                    <div className="mb-1">
                                                        <label className="form-label">
                                                            STATE/PROVINCE
                                                        </label>

                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="state"
                                                            id="state"
                                                            autoComplete="off"
                                                            placeholder="state/province"
                                                            value={input.state}
                                                            onChange={handelchange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-sm-6">
                                                    <div className="mb-1">
                                                        <label className="form-label">
                                                            ZIP/POSTAL CODE
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="pin_code"
                                                            id="pin_code"
                                                            value={input.pin_code}
                                                            onChange={handelchange}
                                                            autoComplete="off"
                                                            placeholder="ZIP/Postal Code"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-sm-6">
                                                    <div className="mb-1">
                                                        <label className="form-label">
                                                            PO BOX
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="pobox"
                                                            id="pobox"
                                                            value={input.pobox}
                                                            onChange={handelchange}
                                                            autoComplete="off"
                                                            placeholder="PO BOX"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-12 my-2">
                                                    <hr className="my-2" />
                                                </div>

                                                {/* <div className="col-12 mb-2">
                                                    <h1 className="modal-title">
                                                        User Preferences
                                                    </h1>
                                                </div> */}
                                                {/* <div className="col-sm-6">
                                                    <div className="mb-1">
                                                        <label htmlFor="">DEFAULT LANGUAGE</label>
                                                        <select className="form-select"
                                                            id="language"
                                                            name="language"
                                                            value={input.language}
                                                            onChange={handelchange}
                                                            disabled={disableFields.language}
                                                        >
                                                            <option value="" disabled hidden>
                                                                Select language
                                                            </option>
                                                            <option>Us English</option>
                                                            <option>German</option>
                                                            <option>Spanish</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="col-sm-6">
                                                    <div className="mb-1">
                                                        <label className="form-label">
                                                            TIMEZONE
                                                        </label>

                                                        <select
                                                            className="form-select"
                                                            name="timezone"
                                                            id="timezone"
                                                            value={input.timezone}
                                                            onChange={handelchange}
                                                            disabled={disableFields.timezone}


                                                        >
                                                            <option value="" disabled hidden>Select Timezone</option>

                                                            {TIMEZONES.map((tz, index) => (
                                                                <option key={index} value={tz}>
                                                                    {tz}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="col-sm-6">
                                                    <div className="mb-1">
                                                        <label htmlFor="">TIME FORMAT</label>
                                                        <select className="form-select"
                                                            id="timefromate"
                                                            name="timefromate"
                                                            value={input.timefromate}
                                                            onChange={handelchange}

                                                            disabled={disableFields.timefromate}


                                                        >
                                                            <option value="" disabled hidden>
                                                                Select time format
                                                            </option>
                                                            <option>24</option>
                                                            <option>12</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="col-sm-6">
                                                    <div className="mb-1">
                                                        <label htmlFor="dateFormatSelect">DATE FORMAT</label>
                                                        <select
                                                            type="date"
                                                            onKeyDown={(e) => e.preventDefault()}
                                                            // className="form-control"
                                                            className="form-select"
                                                            name="dateformate"
                                                            id="dateFormatSelect"
                                                            value={input.dateformate}
                                                            onChange={handelchange}

                                                            disabled={disableFields.dateformate}
                                                        >
                                                            <option value="" disabled hidden>
                                                                Select date format
                                                            </option>
                                                            <option value="DD-MM-YYYY">DD-MM-YYYY</option>
                                                            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                                                        </select>
                                                    </div>
                                                </div> */}
                                                <div className="col-12">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-primary px-4 waves-effect waves-light"
                                                            onClick={onSubmitForm}
                                                            disabled={loading}
                                                        >
                                                            {loading ? "Updating..." : "Update Profile"}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>


                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </div >


        </>
    );
}
